import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  LayoutDashboard, FlaskConical, Layers, Users, Stethoscope, Building2,
  UserCheck, Factory, Receipt, TrendingUp, FileBarChart, DollarSign,
  FileDown, Settings, ArrowLeft, Menu, X, Activity, Lock, CreditCard, FileUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { path: '/vendor/lis', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { type: 'divider', label: 'Rate List' },
  { path: '/vendor/lis/tests', icon: FlaskConical, label: 'Tests' },
  { path: '/vendor/lis/panels', icon: Layers, label: 'Panels & Packages' },
  { path: '/vendor/lis/processing-labs', icon: Factory, label: 'Processing Labs' },
  { type: 'divider', label: 'People' },
  { path: '/vendor/lis/patients', icon: Users, label: 'Patients' },
  { path: '/vendor/lis/doctors', icon: Stethoscope, label: 'Referral Doctors' },
  { path: '/vendor/lis/centres', icon: Building2, label: 'Collection Centres' },
  { path: '/vendor/lis/agents', icon: UserCheck, label: 'Sample Agents' },
  { type: 'divider', label: 'Billing' },
  { path: '/vendor/lis/new-bill', icon: Receipt, label: 'New Bill' },
  { path: '/vendor/lis/cases', icon: FileBarChart, label: 'Cases / Bills' },
  { type: 'divider', label: 'Business' },
  { path: '/vendor/lis/business/daily', icon: TrendingUp, label: 'Daily Report' },
  { path: '/vendor/lis/business/analysis', icon: Activity, label: 'Business Analysis' },
  { path: '/vendor/lis/business/referrals', icon: Stethoscope, label: 'Referral Business' },
  { path: '/vendor/lis/business/expenses', icon: DollarSign, label: 'Expenses' },
  { path: '/vendor/lis/business/export', icon: FileDown, label: 'Data Export' },
  { path: '/vendor/lis/reports', icon: FileUp, label: 'Reports' },
  { type: 'divider', label: 'Settings' },
  { path: '/vendor/lis/settings/bill', icon: Settings, label: 'Bill Template' },
];

export default function LISLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscribing, setSubscribing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [confirmPlan, setConfirmPlan] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('vendor_token');
    if (!token) {
      navigate('/vendor/login');
      return;
    }
    const stored = localStorage.getItem('vendor_user');
    let v = null;
    if (stored) {
      try { v = JSON.parse(stored); } catch (e) { v = null; }
    }
    if (v) {
      if (v.vendor_type !== 'lab') {
        navigate('/vendor/dashboard');
        return;
      }
      setVendor(v);
      checkSubscription(token);
    } else {
      // vendor_user missing/corrupted (e.g. stale localStorage after a partial logout) -
      // refetch the profile instead of getting stuck on "Checking subscription..." forever
      fetch(`${API}/api/b2b/vendors/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.status === 401 || res.status === 403) {
            localStorage.removeItem('vendor_token');
            localStorage.removeItem('vendor_user');
            navigate('/vendor/login');
            return null;
          }
          return res.ok ? res.json() : null;
        })
        .then(data => {
          if (!data) { navigate('/vendor/login'); return; }
          localStorage.setItem('vendor_user', JSON.stringify(data));
          if (data.vendor_type !== 'lab') { navigate('/vendor/dashboard'); return; }
          setVendor(data);
          checkSubscription(token);
        })
        .catch(() => navigate('/vendor/login'));
    }
  }, [navigate]);

  const checkSubscription = async (token) => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [subRes, planRes, walRes] = await Promise.all([
        fetch(`${API}/api/lis/subscription/status`, { headers: h }),
        fetch(`${API}/api/lis/subscription/plans`, { headers: h }),
        fetch(`${API}/api/vendor-wallet/balance`, { headers: h })
      ]);
      if (subRes.status === 401) {
        localStorage.removeItem('vendor_token');
        localStorage.removeItem('vendor_user');
        navigate('/vendor/login');
        return;
      }
      const subData = await subRes.json();
      const planData = await planRes.json();
      const walData = await walRes.json();
      setSubStatus(subData);
      setPlans(planData.plans || []);
      setWalletBalance(walData.balance || 0);
    } catch (e) {
      console.error('Subscription check failed:', e);
    } finally {
      setSubLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    const token = localStorage.getItem('vendor_token');
    setSubscribing(true);
    try {
      const res = await fetch(`${API}/api/lis/subscription/subscribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId })
      });
      const data = await res.json();
      if (res.ok) {
        setSubStatus({ subscribed: true, subscription: data.subscription });
        setWalletBalance(data.wallet_balance);
        setConfirmPlan(null);
      } else {
        alert(data.detail || 'Subscription failed');
      }
    } finally { setSubscribing(false); }
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  if (!vendor) return null;

  // Subscription gate - show paywall if not subscribed
  if (subLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div><p className="text-sm text-slate-500">Checking subscription...</p></div>
      </div>
    );
  }

  if (!subStatus?.subscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="lis-subscription-gate">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FlaskConical className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Lab Management System</h1>
            <p className="text-slate-500">Subscribe to access the complete Lab Information System with billing, patient management, reports, and analytics.</p>
            {subStatus?.expired && <p className="text-amber-600 text-sm mt-2 font-medium">Your subscription has expired. Please renew to continue.</p>}
          </div>

          <div className="grid gap-4 md:grid-cols-2 mb-6">
            {plans.map(plan => (
              <Card key={plan.id} className={`border-2 ${plan.billing_cycle === 'yearly' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200'} cursor-pointer hover:shadow-md transition-shadow`}>
                <CardContent className="p-6 text-center">
                  {plan.billing_cycle === 'yearly' && <span className="inline-block bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-3">BEST VALUE</span>}
                  <h3 className="font-semibold text-slate-800 mb-1">{plan.billing_cycle === 'monthly' ? 'Monthly' : 'Yearly'}</h3>
                  <p className="text-3xl font-bold text-slate-900 mb-1">Rs.{plan.price.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 mb-1">/{plan.billing_cycle === 'monthly' ? 'month' : 'year'}{plan.billing_cycle === 'yearly' && ' (Save Rs.2,989)'}</p>
                  <p className="text-xs text-slate-400 mb-4">{plan.duration_days || (plan.billing_cycle === 'yearly' ? 365 : 30)} days access</p>
                  <Button
                    className="w-full"
                    disabled={walletBalance < plan.price}
                    onClick={() => setConfirmPlan(plan)}
                    data-testid={`subscribe-${plan.billing_cycle}-btn`}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {walletBalance < plan.price ? 'Insufficient Balance' : 'Subscribe Now'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Subscription Confirmation Dialog */}
          <Dialog open={!!confirmPlan} onOpenChange={() => !subscribing && setConfirmPlan(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Confirm Subscription</DialogTitle>
              </DialogHeader>
              {confirmPlan && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <h3 className="font-semibold text-slate-800">{confirmPlan.billing_cycle === 'monthly' ? 'Monthly' : 'Yearly'} Plan</h3>
                    <p className="text-2xl font-bold text-slate-900 mt-1">Rs.{confirmPlan.price.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{confirmPlan.duration_days || (confirmPlan.billing_cycle === 'yearly' ? 365 : 30)} days access</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Current Wallet</span><span className="font-medium">Rs.{walletBalance.toLocaleString()}</span></div>
                    <div className="flex justify-between text-red-600"><span>Deduction</span><span className="font-medium">- Rs.{confirmPlan.price.toLocaleString()}</span></div>
                    <div className="border-t pt-1.5 flex justify-between font-bold"><span>Remaining</span><span className="text-emerald-700">Rs.{(walletBalance - confirmPlan.price).toLocaleString()}</span></div>
                  </div>
                  <p className="text-xs text-amber-600 text-center">Amount will be deducted from your vendor wallet immediately.</p>
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setConfirmPlan(null)} disabled={subscribing}>Cancel</Button>
                <Button onClick={() => handleSubscribe(confirmPlan.id)} disabled={subscribing} data-testid="confirm-subscribe-btn">
                  {subscribing ? 'Processing...' : 'Confirm & Pay'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="text-center">
            <p className="text-sm text-slate-500 mb-2">Wallet Balance: <strong className="text-slate-800">Rs.{walletBalance.toLocaleString()}</strong></p>
            {walletBalance < Math.min(...plans.map(p => p.price), Infinity) && (
              <p className="text-xs text-amber-600">Top up your wallet to subscribe. Go to Vendor Dashboard → Wallet.</p>
            )}
            <Link to="/vendor/dashboard" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Vendor Portal
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden" data-testid="lis-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:relative inset-y-0 lg:inset-y-auto left-0 z-50 w-64 h-screen bg-white border-r border-slate-200 transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} flex flex-col shrink-0`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-slate-800 text-sm">Lab Management</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {NAV_ITEMS.map((item, i) => {
            if (item.type === 'divider') {
              return <div key={`el-${i}`} className="mt-4 mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">{item.label}</div>;
            }
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`lis-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm mb-0.5 transition-colors ${active ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 shrink-0">
          {subStatus?.subscription && (
            <div className="mb-2 px-3 py-1.5 bg-blue-50 rounded-md text-xs text-blue-700">
              <span className="font-medium">{subStatus.subscription.plan_name}</span>
              <br />Expires: {subStatus.subscription.expires_at?.slice(0, 10)}
            </div>
          )}
          <Link
            to="/vendor/dashboard"
            data-testid="lis-back-to-vendor"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Vendor Portal
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-screen min-h-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden mr-3 text-slate-600" data-testid="lis-menu-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">{vendor.name}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Lab</span>
          </div>
        </header>
        <main className="flex-1 min-h-0 p-4 md:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
